import '../bootstrap'
import '../styles/index.scss'
import '../i18n'

import { NextComponentType, NextPageContext } from 'next'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import React, { Fragment, FunctionComponent } from 'react'
import { Provider as ReduxProvider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { WagmiConfig } from 'wagmi'

import DefaultLayout from '../components/Layout'
import store, { persistor } from '../state'
import useApplicationUpdater from '../state/application/updater'
import useListsUpdater from '../state/lists/updater'
import useMulticallUpdater from '../state/multicall/updater'
import { client } from '../wagmi'
import { useAutoConnect } from '../wagmi/hooks/useAutoConnect'
import useInactiveListener from '../wagmi/hooks/useInactiveListener'

/**
 * @return {null}
 */
function GlobalHooks() {
  useApplicationUpdater()
  useInactiveListener()
  useAutoConnect()
  useMulticallUpdater()
  useListsUpdater()
  return null
}
/** Main function for running the Application
 * @param {NextComponentType} Component
 * @param {NextComponentType} pageProps
 * @return {JSX.Element} App
 */
function EmberSwap({
  Component,
  pageProps,
}: AppProps & {
  Component: NextComponentType<NextPageContext> & {
    Guard: FunctionComponent
    Layout: FunctionComponent
    Provider: FunctionComponent
  }
}) {
  /** Allows for conditionally setting a provider to be hoisted per page */
  const Provider: any = Component.Provider || Fragment
  /** Allows for conditionally setting a layout to be hoisted per page */
  const Layout: any = Component.Layout || DefaultLayout
  /** Allows for conditionally setting a guard to be hoisted per page */
  const Guard: any = Component.Guard || Fragment

  return (
    <Fragment>
      <Head>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no"
        />
        <title key="title">EmberSwap</title>
        <meta
          key="description"
          name="description"
          content="The official home of EmberSwap. EmberSwap is a community governed decentralized exchange, providing liquidity and enabling peer-to-peer transactions on the smartBCH Network. Whether you want to swap, farm, stake or just hold, EmberSwap is the DEX for you. EmberSwap breaks up and delivers the mostefficent route for any financial instrument on smartBCH.."
        />
        <meta name="application-name" content="EmberSwap - DeFi on smartBCH" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="EmberSwap - DeFi on smartBCH" />
        <meta
          name="keywords"
          content="incinerate,fire,ember,emberswap,aggregator,decentralized,exchange,burnomics,bitcoin,dogecoin,bch,sbch,smartbch,evm,cash,doge,defi,coinbase,coinmarketcap,coinmarket,sonarcash,sonar,whitebit,coingecko,cryptocurrency,crypto,pancakeswap,dex,ethereum,eth,btc"
        />
        <meta name="format-detection" content="telephone=no" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="msapplication-config" content="/browserconfig.xml" />
        <meta name="msapplication-tap-highlight" content="no" />
        <meta name="theme-color" content="#000" />
        <meta key="twitter:card" name="twitter:card" content="app" />
        <meta key="twitter:title" name="twitter:title" content="EmberSwap - DeFi on smartBCH" />
        <meta key="twitter:url" name="twitter:url" content="https://dex.emberswap.com" />
        <meta
          key="twitter:description"
          name="twitter:description"
          content="The official home of EmberSwap. EmberSwap is a community governed decentralized exchange, providing liquidity and enabling peer-to-peer transactions on the smartBCH Network. Whether you want to swap, farm, stake or just hold, EmberSwap is the DEX for you. EmberSwap breaks up and delivers the mostefficent route for any financial instrument on smartBCH.."
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content="/images/main/icon/icon-512x512.png"
        />
        <meta key="twitter:creator" name="twitter:creator" content="@EmberSwapDEX" />
        <meta key="og:type" property="og:type" content="website" />
        <meta key="og:site_name" property="og:site_name" content="EmberSwap - DeFi on smartBCH" />
        <meta key="og:url" property="og:url" content="https://dex.emberswap.com" />
        <meta key="og:image" property="og:image" content="/images/main/icon/icon-512x512.png" />
        <meta
          key="og:description"
          property="og:description"
          content="The official home of EmberSwap. EmberSwap is a community governed decentralized exchange, providing liquidity and enabling peer-to-peer transactions on the smartBCH Network. Whether you want to swap, farm, stake or just hold, EmberSwap is the DEX for you. EmberSwap breaks up and delivers the mostefficent route for any financial instrument on smartBCH.."
        />
      </Head>

      <WagmiConfig config={client}>
        <ReduxProvider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <Provider>
              <GlobalHooks />
              <Layout>
                <Guard>
                  <Component {...pageProps} />
                </Guard>
              </Layout>
            </Provider>
          </PersistGate>
        </ReduxProvider>
      </WagmiConfig>
    </Fragment>
  )
}

export default EmberSwap
